import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';

import { useEscCancelableContext, ESC_PRIORITY } from '@poseidon/components/contexts';
import { isMacLike, useKey } from '@poseidon/components/hooks/useKey';

import type { SearchFacet, SearchItem } from '~/components/LeftNav/LeftNavSearch/leftNavSearch.model';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { api } from '@/utils/api';
import {
  SEARCH_DEBOUNCE_MS,
  isSearchable,
  searchItemHref,
} from '~/components/LeftNav/LeftNavSearch/leftNavSearch.model';
import { LeftNavSearchPure } from '~/components/LeftNav/LeftNavSearch/LeftNavSearchPure';

/**
 * The left-nav search container: owns the typed query, its debounce, the one `search.global` call, the
 * ⌘K (Ctrl+K) shortcut and navigation.
 *
 * - ⌘K works from anywhere, including while typing in another field, but never while a modal (a Popup —
 *   the ⌘⇧K superadmin palette included) is open: it would pull focus out from under it.
 * - A request whose query is replaced before it answers is aborted (`abortOnUnmount`: React Query cancels
 *   a query with no observers left, and tRPC hands its signal to fetch).
 * - Only an answer for exactly what is in the box is shown or selectable — while the next answer is
 *   pending the popover says "Searching…" instead of offering the previous query's rows to Enter.
 */
export function LeftNavSearch({ teamId, minimized }: { teamId: string; minimized: boolean }) {
  const router = useRouter();
  const { hasOpenAtOrAbove } = useEscCancelableContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [facet, setFacet] = useState<SearchFacet>('all');
  const [activeId, setActiveId] = useState<string | null>(null);
  const setSearchDebounced = useDebouncedCallback(setSearch, SEARCH_DEBOUNCE_MS);

  const trimmedSearch = search.trim();
  const searchQuery = api.search.global.useQuery(
    { teamId, query: trimmedSearch },
    {
      // Nothing searchable ("::") never reaches the server.
      enabled: isSearchable(trimmedSearch),
      staleTime: 30 * 1000,
      trpc: { abortOnUnmount: true },
    },
  );
  const upToDate = query.trim() === trimmedSearch;
  const results = upToDate ? searchQuery.data : undefined;
  const isLoading = !upToDate || searchQuery.isFetching;

  const reset = () => {
    setOpen(false);
    setQuery('');
    setSearch('');
    setActiveId(null);
    setFacet('all');
    inputRef.current?.blur();
  };

  const onQueryChange = (next: string) => {
    setQuery(next);
    setSearchDebounced(next);
    setActiveId(null);
    setFacet('all');
  };

  const focusSearch = useCallback(
    (e: KeyboardEvent) => {
      // Always claim the shortcut — even when it does nothing here — so Ctrl+K never falls through to the
      // browser's own (Windows/Linux Chrome focuses the address bar's search).
      e.preventDefault();
      if (hasOpenAtOrAbove(ESC_PRIORITY.INTERSTITIAL)) return;
      setOpen(true);
      // Collapsed nav: the box lives in the popover, which mounts on the next frame.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    },
    [hasOpenAtOrAbove],
  );
  useKey('k', focusSearch, { metaWindowsCtrlKey: true, exactShift: false, allowInInputs: true });

  return (
    <LeftNavSearchPure
      minimized={minimized}
      isMac={isMacLike()}
      query={query}
      onQueryChange={onQueryChange}
      open={open}
      onOpenChange={setOpen}
      results={results}
      isLoading={isLoading}
      facet={facet}
      onFacetChange={(next) => {
        setFacet(next);
        setActiveId(null);
      }}
      activeId={activeId}
      onActiveIdChange={setActiveId}
      onSelect={(item: SearchItem) => {
        const href = searchItemHref(item, query);
        reset();
        void router.push(href);
      }}
      inputRef={inputRef}
    />
  );
}
